import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Search, 
  Plus, 
  Download,
  FileText,
  File,
  DollarSign,
  Edit,
  Trash2,
  Calendar,
  Receipt
} from 'lucide-react';
import { generateDonationsPdf } from '../utils/pdfGenerator';
import * as XLSX from 'xlsx';
import DonationFormModal from './DonationFormModal';
import DonationReceiptModal from './DonationReceiptModal';
import { getApiUrl } from '../utils/apiConfig';
// Utilidad para exportar a Excel
function exportDonationsExcel(data, totalFiltrado = null) {
  // Utiliza el mismo formateo de método que en la tabla/pdf
  const getPaymentMethodLabel = (method) => {
    const labels = {
      wechat: 'WeChat Pay',
      alipay: 'Efectivo',
      bank: 'Transferencia Bancaria',
      cash: 'Efectivo'
    };
    return labels[method] || method;
  };
  // Formateo de fecha corta
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.slice(0, 10);
  };
  // Formateo de monto chileno sin decimales
  const formatCLP = (amount) => {
    return Number(amount).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  
  const wsData = [
    ['Donante', 'Monto', 'Fecha', 'Método', 'Notas'],
    ...data.map(d => [
      d.donorName,
      formatCLP(d.amount),
      formatShortDate(d.donationDate),
      getPaymentMethodLabel(d.paymentMethod),
      d.notes
    ])
  ];
  
  // Agregar fila de total si se proporciona
  if (totalFiltrado !== null) {
    wsData.push([]); // Fila vacía
    wsData.push(['TOTAL FILTRADO:', formatCLP(totalFiltrado), '', '', '']);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Donaciones');
  XLSX.writeFile(wb, 'donaciones.xlsx');
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - offset);
  return d.toISOString().slice(0, 10);
}

const Donations = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  // Estado para paginación
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationToEdit, setDonationToEdit] = useState(null);

 const [members, setMembers] = useState([]);

  const [donations, setDonations] = useState([]);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
const [selectedDonation, setSelectedDonation] = useState(null);
const [filterYear, setFilterYear] = useState('all');
const [filterMonth, setFilterMonth] = useState('all');
const handleViewReceipt = (donation) => {
  setSelectedDonation(donation);
  setShowReceiptModal(true);
};
  // Cargar donaciones desde la base de datos
  const fetchDonations = () => {
  fetch(getApiUrl('/api/donaciones'))
      .then(res => res.json())
      .then(data => setDonations(Array.isArray(data) ? data : []));
  };

  const fetchMembers = () => {
  fetch(getApiUrl('/api/miembros'))
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]));
};

  useEffect(() => {
    fetchDonations();
    fetchMembers();
  }, []);

    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYear = now.getFullYear();
  
  const getPaymentMethodLabel = (method) => {
    const labels = {
      wechat: 'WeChat Pay',
      alipay: 'Efectivo',
      bank: 'Transferencia Bancaria',
      };
    return labels[method] || method;
  };

const filteredDonations = donations.filter(donation => {
  // Si el usuario es 'user', solo mostrar sus propias donaciones
  if (currentUser.role === 'user') {
    if (donation.donorId !== currentUser.id) return false;
  }
  const member = members.find(m => m.id === donation.donorId);
  const rutSocio = member && member.rut ? member.rut.toLowerCase() : '';
  const matchesSearch =
    donation.donorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (donation.notes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    rutSocio.includes(searchTerm.toLowerCase());
  const matchesMethod = filterMethod === 'all' || donation.paymentMethod === filterMethod;
  // Filtro por año
  let matchesYear = true;
  if (filterYear !== 'all' && donation.donationDate) {
    matchesYear = donation.donationDate.startsWith(filterYear);
  }
  // Filtro por mes
  let matchesMonth = true;
  if (filterMonth !== 'all' && donation.donationDate) {
    const month = donation.donationDate.split('-')[1];
    matchesMonth = month === filterMonth;
  }
  return matchesSearch && matchesMethod && matchesYear && matchesMonth;
});

  const paginatedDonations = filteredDonations.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  
  // Calcular monto filtrado
  const filteredAmount = filteredDonations.reduce((sum, donation) => sum + Number(donation.amount), 0);
  
  // Estadísticas solo del usuario logeado si es rol 'user'
  let userDonations = donations;
  if (currentUser.role === 'user') {
    userDonations = donations.filter(d => d.donorId === currentUser.id);
  }
  const totalDonatedAmount = userDonations.reduce((sum, d) => sum + Number(d.amount), 0);
  const donationsThisMonth = userDonations.filter(d =>
    d.donationDate &&
    d.donationDate.startsWith(`${currentYear}-${currentMonth}`)
  ).length;

  const handleExportPdf = () => {
    generateDonationsPdf(filteredDonations, filteredAmount);
  };

  const handleAddDonation = () => {
    setDonationToEdit(null);
    setShowDonationModal(true);
  };

  const handleEditDonation = (donation) => {
    setDonationToEdit({
      ...donation,
      donationDate: formatDate(donation.donationDate)
    });
    setShowDonationModal(true);
  };

  const handleDeleteDonation = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta donación?')) {
  fetch(getApiUrl(`/api/donaciones/${id}`), {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(() => fetchDonations());
    }
  };

  // Guardar (crear o editar) donación
  const handleSaveDonation = (newDonationData) => {
    let donorName = newDonationData.donorName;
    let donorId = newDonationData.donorId;
    // Si se seleccionó un socio, usa su nombre
    if (donorId) {
      const member = members.find(m => m.id === parseInt(donorId));
      donorName = member ? member.name : donorName;
    }
    const donationWithName = { ...newDonationData, donorName, donorId: donorId ? parseInt(donorId) : null };

  fetch(getApiUrl('/api/donaciones'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(donationWithName)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(`Error al guardar donación: ${data.error}`);
        } else {
          alert('Donación guardada correctamente');
          setShowDonationModal(false);
          fetchDonations();
        }
      })
      .catch(err => {
        console.error('Error al guardar donación:', err);
        alert('Error de conexión al guardar la donación. Por favor, intente nuevamente.');
      });
  };

  return (
  <div className="space-y-6 px-2 sm:px-4 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Gestión de Donaciones
          </h1>
          <p className="text-gray-600">Administre las contribuciones recibidas por su organización</p>
        </div>
        
        {currentUser.role !== 'user' && (
          <motion.button
            onClick={handleAddDonation}
            className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus className="w-5 h-5" />
            Registrar Donación
          </motion.button>
        )}
      </motion.div>


      {/* Donation Stats */}
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { 
            title: 'Monto Filtrado', 
            value: `$${filteredAmount.toLocaleString()}`, 
            icon: DollarSign, 
            color: 'from-purple-500 to-indigo-500',
            bgColor: 'from-purple-50 to-indigo-50'
          },
          { 
            title: 'Total Donado', 
            value: `$${totalDonatedAmount.toLocaleString()}`, 
            icon: DollarSign, 
            color: 'from-green-500 to-emerald-500',
            bgColor: 'from-green-50 to-emerald-50'
          },
          { 
            title: 'Donaciones Registradas', 
            value: userDonations.length, 
            icon: Heart, 
            color: 'from-red-500 to-pink-500',
            bgColor: 'from-red-50 to-pink-50'
          },
          { 
            title: 'Donaciones este Mes', 
            value: donationsThisMonth, 
            icon: Calendar, 
            color: 'from-blue-500 to-cyan-500',
            bgColor: 'from-blue-50 to-cyan-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} border border-gray-200/50 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.title}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Filters and Search */}
      <motion.div
  className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por rut, donante o notas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>
          
          <div className="flex gap-3 flex-wrap">
  <select
    value={filterMethod}
    onChange={(e) => setFilterMethod(e.target.value)}
    className="px-4 py-3 bg-gradient-to-r from-gray-50 to-blue-50 border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300 font-semibold text-blue-700"
  >
    <option value="all">Todos los métodos</option>
    <option value="wechat">WeChat Pay</option>
  <option value="alipay">Efectivo</option>
    <option value="bank">Transferencia Bancaria</option>
    <option value="cash">Efectivo</option>
  </select>
  <select
    value={filterYear}
    onChange={e => { setFilterYear(e.target.value); setPage(0); }}
    className="px-4 py-3 bg-gradient-to-r from-gray-50 to-green-50 border border-green-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all duration-300 font-semibold text-green-700"
  >
    <option value="all">Todos los años</option>
    {[...new Set(donations.map(d => d.donationDate?.slice(0,4)).filter(Boolean))].map(year => (
      <option key={year} value={year}>{year}</option>
    ))}
  </select>
  <select
    value={filterMonth}
    onChange={e => { setFilterMonth(e.target.value); setPage(0); }}
    className="px-4 py-3 bg-gradient-to-r from-gray-50 to-yellow-50 border border-yellow-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500 transition-all duration-300 font-semibold text-yellow-700"
  >
    <option value="all">Todos los meses</option>
    {[...Array(12)].map((_, i) => {
      const month = String(i+1).padStart(2, '0');
      return <option key={month} value={month}>{month}</option>;
    })}
  </select>
            <motion.button
              onClick={handleExportPdf}
              className="px-4 py-3 bg-red-100 hover:bg-red-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Exportar PDF"
            >
                <File className="w-5 h-5 text-red-600" />
            </motion.button>
            <motion.button
              onClick={() => exportDonationsExcel(filteredDonations, filteredAmount)}
              className="px-4 py-3 bg-green-100 hover:bg-green-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              title="Exportar Excel"
            >
              <FileText className="w-5 h-5 text-green-600" />
            </motion.button>
</div>
        </div>
      </motion.div>

      {/* Paginación independiente */}
  <div className="flex justify-start items-center gap-2 mt-4 mb-2">
        <select
          value={rowsPerPage}
          onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
        >
          {[10,20,50,100,500,1000].map(n => (
            <option key={n} value={n}>{n} resultados</option>
          ))}
        </select>
        <button
          className="px-3 py-2 rounded-xl bg-gray-100 text-gray-600 font-semibold disabled:opacity-50"
          onClick={() => setPage(page - 1)}
          disabled={page === 0}
        >
          &lt; Anterior
        </button>
        <span className="font-semibold text-gray-700">Página {page + 1} de {Math.max(1, Math.ceil(filteredDonations.length / rowsPerPage))}</span>
        <button
          className="px-3 py-2 rounded-xl bg-gray-100 text-gray-600 font-semibold disabled:opacity-50"
          onClick={() => setPage(page + 1)}
          disabled={(page + 1) * rowsPerPage >= filteredDonations.length}
        >
          Siguiente &gt;
        </button>
      </div>
      <motion.div
  className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-x-auto overflow-y-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="min-w-[600px] w-full text-sm">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Donante</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Monto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Método</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Notas</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedDonations.map((donation, index) => (
                <motion.tr
                  key={donation.id}
                  className="hover:bg-gray-50/50 transition-colors duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.05 }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-pink-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {donation.donorName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{donation.donorName}</p>
                        {/* Mostrar el rut del socio usando donorId */}
                        {(() => {
                          const member = members.find(m => m.id === donation.donorId);
                          return member && member.rut ? (
                            <p className="text-sm text-gray-500">RUT: {member.rut}</p>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-900">${Number(donation.amount).toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(donation.donationDate)}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                      {getPaymentMethodLabel(donation.paymentMethod)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                    {donation.notes || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <motion.button
                        onClick={() => handleViewReceipt(donation)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Ver comprobante"
                      >
                        <Receipt className="w-4 h-4" />
                      </motion.button>
                      {currentUser.role !== 'user' && (
                        <>
                          <motion.button
                            onClick={() => handleEditDonation(donation)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteDonation(donation.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      <DonationFormModal
        isOpen={showDonationModal}
        onClose={() => setShowDonationModal(false)}
        onSave={handleSaveDonation}
        donationToEdit={donationToEdit}
        members={members}
      />
      
      <DonationReceiptModal
  isOpen={showReceiptModal}
  onClose={() => setShowReceiptModal(false)}
  donation={selectedDonation}
/>
    </div>
  );
};

export default Donations;